// Ported from vlookup-dramatic/components/site-header.tsx by scripts/revamp/port-design.js
import Link from "./link";
import { usePathname } from "../hooks/use-pathname";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { userLogout } from "../../actions/userActions";
import { checkConform } from "../../utils/helpers";
import { ArrowUpRight, ChevronDown, Menu } from "../lib/icons";
import { site } from "../lib/content";
import { Logo } from "./logo";
import { ButtonLink } from "./button-link";
import { buttonVariants } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, } from "./ui/sheet";
import { cn } from "../lib/utils";
function DesktopNavItem({ item, active }) {
    const pathname = usePathname();
    const [open, setOpen] = useState(false);
    // The panel used to open purely on :hover / :focus-within. Clicking an item
    // leaves the anchor focused, so focus-within held the panel open on top of
    // the page you had just navigated to. Driving it from state lets the click
    // close it, and the pathname effect covers back/forward navigation.
    useEffect(() => {
        setOpen(false);
    }, [pathname]);
    const linkCls = cn("text-[12px] font-bold uppercase tracking-[0.12em] text-brand-navy/70 transition-colors hover:text-brand-red", active && "text-brand-red");
    if (!item.children) {
        return (<Link href={item.href} className={linkCls}>
        {item.label}
      </Link>);
    }
    return (<div className="group relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} 
    // React's onFocus/onBlur bubble, so these fire for the trigger and every
    // item inside — keyboard users get the same panel mouse users get.
    onFocus={() => setOpen(true)} onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
                setOpen(false);
            }
        }} onKeyDown={(e) => {
            if (e.key === "Escape")
                setOpen(false);
        }}>
      {/* Parent label is a button, not a link — the children in the dropdown
            are the real destinations. Clicking the label toggles the panel. */}
      <button type="button" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cn(linkCls, "flex cursor-default items-center gap-1 py-7")}>
        {item.label}
        <ChevronDown className={cn("size-3.5 transition-transform duration-200", open && "rotate-180")}/>
      </button>

      {/* Dropdown panel */}
      <div className={cn("absolute left-1/2 top-full z-50 w-[400px] -translate-x-1/2 transition-all duration-200", open
            ? "visible translate-y-0 opacity-100"
            : "invisible translate-y-2 opacity-0")}>
        <div className="overflow-hidden rounded-3xl border border-brand-navy/10 bg-white p-2 shadow-xl">
          {item.children.map((child) => (<Link key={child.label} href={child.href} 
        // Closes the panel even when the target is the page you are
        // already on, where the pathname effect never fires.
        onClick={() => setOpen(false)} className="group/item flex items-start justify-between gap-3 rounded-2xl px-5 py-4 transition-colors hover:bg-cream">
              <div className="min-w-0">
                <div className="font-display text-[17px] font-semibold text-brand-navy transition-colors group-hover/item:text-brand-red">
                  {child.label}
                </div>
                <p className="mt-1 text-[13px] leading-snug text-brand-navy/60">
                  {child.desc}
                </p>
              </div>
              <span className="mt-1 flex size-7 shrink-0 items-center justify-center rounded-full border border-brand-navy/15 text-brand-navy transition-all group-hover/item:border-brand-red group-hover/item:bg-brand-red group-hover/item:text-white">
                <ArrowUpRight className="size-3.5"/>
              </span>
            </Link>))}
        </div>
      </div>
    </div>);
}
export function SiteHeader() {
    const pathname = usePathname();
    const [open, setOpen] = useState(false);
    // Signed-in users get "My Dashboard" instead of Join Free / Log In (same as the old header).
    const userInfo = useSelector((s) => s.userInfo) || {};
    const loggedIn = !!userInfo.id;
    const dispatch = useDispatch();
    const history = useHistory();
    // QA row 30: logout available from every page, not only the dashboard
    // sidebar. Same confirm + wiring as the sidebar/old header.
    const onLogout = () => {
        checkConform(() => {
            localStorage.setItem("authToken", "");
            localStorage.clear();
            dispatch(userLogout({}));
            history.push("/login");
        }, "Are you sure you want to logout?");
    };
    const isActive = (item) => (item.href !== "/" && pathname.startsWith(item.href.split("#")[0])) ||
        (item.children?.some((c) => c.href !== "/" && pathname.startsWith(c.href.split("#")[0])) ??
            false);
    return (<header className="sticky top-0 z-50 border-b border-brand-navy/10 bg-cream/80 backdrop-blur-md">
      <div className="wrap flex h-[76px] items-center gap-6">
        <Logo priority/>

        <nav className="ml-8 hidden items-center gap-7 lg:flex">
          {site.nav.map((item) => (<DesktopNavItem key={item.label} item={item} active={isActive(item)}/>))}
        </nav>

        {/* Far right — Join Free / Log In, or My Dashboard once signed in */}
        <div className="ml-auto hidden items-center gap-3 md:flex">
          {loggedIn ? (<>
              <ButtonLink href="/manage-profile" className="group rounded-full bg-brand-red px-5 py-5 font-bold text-white hover:bg-brand-red-dark">
                My Dashboard
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
              </ButtonLink>
              <button type="button" onClick={onLogout} className="cursor-pointer text-[12px] font-bold uppercase tracking-[0.12em] text-brand-navy/70 transition-colors hover:text-brand-red">
                Log Out
              </button>
            </>) : (<>
              <ButtonLink href="/#join" className="group rounded-full bg-brand-red px-5 py-5 font-bold text-white hover:bg-brand-red-dark">
                Join Free
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
              </ButtonLink>
              <Link href="/#login" className="text-[12px] font-bold uppercase tracking-[0.12em] text-brand-navy/70 transition-colors hover:text-brand-red">
                Log In
              </Link>
            </>)}
        </div>

        {/* Mobile */}
        <div className="ml-auto lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger aria-label="Open menu" className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "rounded-full")}>
              <Menu className="size-6"/>
            </SheetTrigger>
            <SheetContent side="right" className="w-[310px] bg-cream">
              <SheetHeader>
                <SheetTitle>
                  <Logo />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-4 flex flex-col gap-1 px-4">
                {site.nav.map((item) => (<div key={item.label}>
                    {item.children ? (<span className="block cursor-default rounded-xl px-3 py-3 text-sm font-bold uppercase tracking-[0.1em] text-brand-navy/50">
                        {item.label}
                      </span>) : (<Link href={item.href} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-3 text-sm font-bold uppercase tracking-[0.1em] text-brand-navy/80 hover:bg-brand-navy/5">
                        {item.label}
                      </Link>)}
                    {item.children?.map((child) => (<Link key={child.label} href={child.href} onClick={() => setOpen(false)} className="block rounded-xl py-2.5 pl-8 pr-3 text-[13px] font-semibold text-brand-navy/60 hover:bg-brand-navy/5">
                        {child.label}
                      </Link>))}
                  </div>))}
                <div className="mt-4 flex flex-col gap-2 px-3">
                  {loggedIn ? (<>
                      <ButtonLink href="/manage-profile" onClick={() => setOpen(false)} className="rounded-full bg-brand-red font-bold text-white hover:bg-brand-red-dark">
                        My Dashboard
                      </ButtonLink>
                      <button type="button" onClick={() => { setOpen(false); onLogout(); }} className="cursor-pointer rounded-full border border-brand-navy/30 px-4 py-2.5 text-sm font-bold uppercase tracking-[0.08em] text-brand-navy transition-colors hover:border-brand-red hover:text-brand-red">
                        Log Out
                      </button>
                    </>) : (<>
                      <ButtonLink href="/#join" onClick={() => setOpen(false)} className="rounded-full bg-brand-red font-bold text-white hover:bg-brand-red-dark">
                        Join Free
                      </ButtonLink>
                      <ButtonLink href="/#login" variant="outline" onClick={() => setOpen(false)} className="rounded-full border-brand-navy/30 font-bold uppercase tracking-[0.08em] text-brand-navy">
                        Log In
                      </ButtonLink>
                    </>)}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>);
}
